import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { benchmarkService } from '../../services/benchmarkService';
import { BenchmarkSummaryCards } from '../../components/benchmark/BenchmarkSummaryCards';
import { BenchmarkStatsByTagCards } from '../../components/benchmark/BenchmarkStatsByTagCards';
import { PerfectDocumentsCard } from '../../components/benchmark/PerfectDocumentsCard';
import { ErrorsBySeverityCard } from '../../components/benchmark/ErrorsBySeverityCard';
import { TokenUsageCard } from '../../components/benchmark/TokenUsageCard';
import { FieldAccuracyChart } from '../../components/benchmark/FieldAccuracyChart';
import { WorstFieldsTable } from '../../components/benchmark/WorstFieldsTable';
import { NewRunModal } from '../../components/benchmark/NewRunModal';
import { BenchmarkProgressBar } from '../../components/benchmark/BenchmarkProgressBar';
import { LockRunButton } from '../../components/benchmark/LockRunButton';
import { DocumentDrilldownModal } from '../../components/benchmark/DocumentDrilldownModal';
import { ExportButtons } from '../../components/benchmark/ExportButtons';
import { ModelConfigCard } from '../../components/benchmark/ModelConfigCard';
import { RunSelector } from '../../components/benchmark/RunSelector';
import { RunHistoryOverlay } from '../../components/benchmark/RunHistoryOverlay';
import type { BenchmarkRun, BenchmarkReport, BenchmarkDocument, BenchmarkStatsByTag } from '../../types/benchmark';
import { PageContainer } from '../../components/layout';

export function AnalysePage() {
  const [runs, setRuns] = useState<BenchmarkRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [report, setReport] = useState<BenchmarkReport | null>(null);
  const [statsByTag, setStatsByTag] = useState<BenchmarkStatsByTag | null>(null);
  const [documents, setDocuments] = useState<BenchmarkDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewRunModal, setShowNewRunModal] = useState(false);
  const [showRunHistoryOverlay, setShowRunHistoryOverlay] = useState(false);
  const [runningRunId, setRunningRunId] = useState<string | null>(null);
  const [drilldownDocId, setDrilldownDocId] = useState<number | null>(null);

  useEffect(() => {
    loadRuns();
  }, []);

  useEffect(() => {
    if (selectedRunId) {
      const selectedRun = runs.find(r => r.id === selectedRunId);
      // Only load report and documents for completed runs
      if (selectedRun && selectedRun.status === 'completed') {
        loadReport(selectedRunId);
        loadDocuments(selectedRunId);
        loadStatsByTag(selectedRunId);
      } else {
        // Clear report and documents if selecting a non-completed run
        setReport(null);
        setStatsByTag(null);
        setDocuments([]);
      }
    }
  }, [selectedRunId, runs]);


  // Auto-refresh runs list when there are running runs
  useEffect(() => {
    const hasRunningRuns = runs.some(r => r.status === 'running' || r.status === 'pending');

    if (!hasRunningRuns) {
      return;
    }

    const interval = setInterval(() => {
      refreshRunsQuietly();
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(interval);
  }, [runs]);

  const refreshRunsQuietly = async () => {
    try {
      const data = await benchmarkService.getRuns();
      setRuns(data);
    } catch (err) {
      // Silent failure during auto-refresh
      console.error('Failed to refresh runs:', err);
    }
  };

  const loadRuns = async () => {
    try {
      setLoading(true);
      const data = await benchmarkService.getRuns();
      setRuns(data);

      // Select most recent completed run by default
      const completedRuns = data.filter(r => r.status === 'completed');
      if (completedRuns.length > 0 && !selectedRunId) {
        setSelectedRunId(completedRuns[0].id);
      }

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async (runId: string) => {
    try {
      const data = await benchmarkService.getReport(runId);
      setReport(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    }
  };

  const loadStatsByTag = async (runId: string) => {
    try {
      const data = await benchmarkService.getStatsByTag(runId);
      setStatsByTag(data);
    } catch (err) {
      console.error('Failed to load stats by tag:', err);
      setStatsByTag(null);
    }
  };

  const loadDocuments = async (runId: string) => {
    try {
      const data = await benchmarkService.getDocuments(runId);
      setDocuments(data);
    } catch (err) {
      console.error('Failed to load documents:', err);
    }
  };


  const handleViewDocument = (docId: number) => {
    setDrilldownDocId(docId);
  };

  const handleViewFirstDocument = () => {
    if (documents.length > 0) {
      setDrilldownDocId(documents[0].id);
    }
  };

  const handleNavigateDocument = (direction: 'prev' | 'next') => {
    if (!drilldownDocId) return;

    const currentIndex = documents.findIndex(d => d.id === drilldownDocId);
    if (currentIndex === -1) return;

    if (direction === 'prev' && currentIndex > 0) {
      setDrilldownDocId(documents[currentIndex - 1].id);
    } else if (direction === 'next' && currentIndex < documents.length - 1) {
      setDrilldownDocId(documents[currentIndex + 1].id);
    }
  };

  const getDrilldownNavigation = () => {
    if (!drilldownDocId) return { canPrev: false, canNext: false };

    const currentIndex = documents.findIndex(d => d.id === drilldownDocId);
    return {
      canPrev: currentIndex > 0,
      canNext: currentIndex < documents.length - 1,
    };
  };

  const handleProgressComplete = () => {
    refreshRunsQuietly();
    if (runningRunId) {
      setSelectedRunId(runningRunId);
    }
    setRunningRunId(null);
  };

  const handleLockRun = async (runId: string, locked: boolean) => {
    try {
      await benchmarkService.lockRun(runId, locked);
      toast.success(locked ? 'Run verrouillé' : 'Run déverrouillé');
      refreshRunsQuietly();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to lock run');
    }
  };

  const handleDeleteRun = async (runId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce run?')) {
      return;
    }

    try {
      await benchmarkService.deleteRun(runId);
      toast.success('Run supprimé');
      if (selectedRunId === runId) {
        setSelectedRunId(null);
        setReport(null);
      }
      refreshRunsQuietly();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete run');
    }
  };

  const handleDownloadLogs = async (runId: string) => {
    try {
      await benchmarkService.downloadAllLogs(runId);
      toast.success('Téléchargement démarré');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to download logs');
    }
  };

  const handleCancelRun = async (runId: string) => {
    try {
      await benchmarkService.cancelRun(runId);
      toast.success('Run annulée');
      refreshRunsQuietly();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors de l\'annulation');
    }
  };

  const selectedRun = runs.find(r => r.id === selectedRunId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-900">Benchmark - Analyse</h1>

          <div className="flex items-center gap-4">
            {selectedRunId && <ExportButtons runId={selectedRunId} />}

            {selectedRun && (
              <LockRunButton
                isLocked={selectedRun.is_locked}
                onToggle={() => handleLockRun(selectedRun.id, !selectedRun.is_locked)}
              />
            )}

            <button
              className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600"
              onClick={() => setShowNewRunModal(true)}
            >
              + Nouveau run
            </button>
          </div>
        </div>

        {/* New Compact Run Selector */}
        <RunSelector
          selectedRun={selectedRun}
          onClick={() => setShowRunHistoryOverlay(true)}
        />
      </div>

      {/* Configuration du modèle (collapsible) — montée juste sous le sélecteur
          de run. Ne dépend que de selectedRun, donc visible dès qu'un run est
          choisi (même pending/running/failed), pas seulement avec un rapport. */}
      {selectedRun && (
        <div className="mb-6">
          <ModelConfigCard run={selectedRun} />
        </div>
      )}

      {/* Progress bar for running benchmarks */}
      {runningRunId && (
        <div className="mb-6">
          <BenchmarkProgressBar runId={runningRunId} onComplete={handleProgressComplete} />
        </div>
      )}

      {!report && selectedRunId && !runningRunId && (
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <p className="text-blue-800 mb-4">
            {selectedRun?.status === 'pending' && '⏳ Run en attente de démarrage...'}
            {selectedRun?.status === 'running' && '🔄 Run en cours d\'exécution...'}
            {selectedRun?.status === 'failed' && '❌ Run échoué'}
            {!selectedRun && 'Sélectionnez un run terminé pour voir le rapport'}
          </p>
          {(selectedRun?.status === 'running' || selectedRun?.status === 'pending') && (
            <BenchmarkProgressBar runId={selectedRunId} onComplete={handleProgressComplete} />
          )}
        </div>
      )}

      {!report && !selectedRunId && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
          <p className="text-yellow-800">Sélectionnez un run pour voir le rapport</p>
        </div>
      )}

      {report && selectedRun && (
        <>
          {/* PROD1.7.8 N1 — 3 cards stats par tag (global/standards/complexes) */}
          {statsByTag && <BenchmarkStatsByTagCards stats={statsByTag} />}

          {/* ROW 1: Main Metrics (6 cards) */}
          <BenchmarkSummaryCards report={report} onViewFirstDocument={handleViewFirstDocument} />

          {/* ROW 2: Quality (3 cards) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <PerfectDocumentsCard report={report} />
            <ErrorsBySeverityCard report={report} />
            <TokenUsageCard report={report} />
          </div>

          {/* ROW 3: Field Analysis (2 cards) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <FieldAccuracyChart fields={report.by_field} />
            <WorstFieldsTable report={report} limit={10} />
          </div>
        </>
      )}

      {/* New Run Modal */}
      <NewRunModal
        isOpen={showNewRunModal}
        onClose={() => setShowNewRunModal(false)}
        onSuccess={(runId: string) => {
          setShowNewRunModal(false);
          setSelectedRunId(runId);
          setRunningRunId(runId);
          loadRuns();
        }}
      />

      {/* Run History Overlay */}
      <RunHistoryOverlay
        isOpen={showRunHistoryOverlay}
        onClose={() => setShowRunHistoryOverlay(false)}
        runs={runs}
        selectedRunId={selectedRunId}
        onSelectRun={setSelectedRunId}
        onViewDocument={handleViewDocument}
        onDeleteRun={handleDeleteRun}
        onLockRun={handleLockRun}
        onCancelRun={handleCancelRun}
        onDownloadLogs={handleDownloadLogs}
      />

      {/* Document Drilldown Modal */}
      {drilldownDocId && selectedRunId && (
        <DocumentDrilldownModal
          isOpen={!!drilldownDocId}
          onClose={() => setDrilldownDocId(null)}
          runId={selectedRunId}
          documentId={drilldownDocId}
          onNavigate={handleNavigateDocument}
          canNavigatePrev={getDrilldownNavigation().canPrev}
          canNavigateNext={getDrilldownNavigation().canNext}
        />
      )}
    </PageContainer>
  );
}
